'use client';

/**
 * SectionRenderer - Unified section rendering with multiple modes
 * 
 * This component supports three rendering modes (in priority order):
 * 1. NEUROPLASTIC V2 MODE: Uses unified NeuroplasticSection component
 * 2. NEUROPLASTIC V1 MODE: Renders from JSON templates via TemplateEngine
 * 3. LEGACY MODE (fallback): Uses hardcoded SectionComponentRenderer
 * 
 * Mode selection:
 * - V2: When useNeuroplasticV2=true (can be enabled globally or per-section)
 * - V1: When section has template OR type has template generator
 * - Legacy: Fallback for sections without templates
 */

import React, { useMemo, useEffect, useState } from 'react';
import { Section, Background, ComponentStyles } from '@/types';
import { ResponsiveBackgroundImage } from '@/types/sections';
import SectionComponentRenderer from './SectionComponentRenderer';
import SectionContentWrapper from './SectionContentWrapper';
import SectionWrapper from './SectionWrapper';
import { useBackgroundOptimization } from '@/hooks/useBackgroundOptimization';
import { SectionProvider } from '@/contexts/SectionContext';
import { mediaCache } from '@/lib/media-cache';
import { NeuroplasticSectionRenderer, NeuroplasticSection } from './NeuroplasticSectionRenderer';
import { NeuroplasticSection as NeuroplasticSectionV2, transformToNeuroplasticConfig } from './neuroplastic';
import { extractFallbackImagesFromSection } from '@/lib/video-fallback-utils';
// PERFORMANCE: Lazy load template-generators (18KB) - only needed when checking for template generators
// This is only used to check if a section type has a generator, not to generate templates on client
let templateGeneratorsModule: typeof import('@/scripts/template-generators') | null = null;
const getTemplateGenerator = async (sectionType: string) => {
  if (!templateGeneratorsModule) {
    templateGeneratorsModule = await import('@/scripts/template-generators');
  }
  return templateGeneratorsModule.generateTemplateForSectionType(sectionType);
};
import { type MediaPriorityLevel } from '@/lib/page-priority';

export interface SectionRendererProps {
  section: Section;
  isFirstSection?: boolean;
  sectionIndex?: number;
  mediaUrls?: string[]; // Media URLs extracted from this section
  useNeuroplasticV2?: boolean; // Enable unified neuroplastic section rendering
  mediaPriority?: MediaPriorityLevel; // Media loading priority for this section
}

/**
 * Compute styles from section background and styles
 */
function computeSectionStyles(
  background?: Background | Record<string, unknown> | null,
  styles?: ComponentStyles | Record<string, unknown> | null,
  backgroundImage?: ResponsiveBackgroundImage | string | undefined
): React.CSSProperties {
  const computedStyles: React.CSSProperties = {};

  // Process responsive background image (from sectionData) - takes priority
  if (backgroundImage) {
    // Background images are handled by individual section components
    // This is just for fallback background styling
  }

  // Process background
  if (background) {
    const bg = background as Background | Record<string, unknown>;
    
    // Handle Background type with type/value structure
    if (bg && typeof bg === 'object' && 'type' in bg && 'value' in bg) {
      const bgTyped = bg as Background;
      if (bgTyped.type === 'COLOR') {
        const color = bgTyped.value;
        const opacity = bgTyped.opacity ?? 1;
        computedStyles.backgroundColor = opacity < 1 
          ? `rgba(${hexToRgb(color)?.join(', ') || '0, 0, 0'}, ${opacity})`
          : color;
      } else if (bgTyped.type === 'IMAGE') {
        computedStyles.backgroundImage = `url(${bgTyped.value})`;
        computedStyles.backgroundSize = 'cover';
        computedStyles.backgroundPosition = 'center';
        computedStyles.backgroundRepeat = 'no-repeat';
      }
    } else if (bg && typeof bg === 'object') {
      // Handle raw JSON background object (from database)
      Object.assign(computedStyles, bg);
    }
  }

  // Process styles
  if (styles) {
    const stylesObj = styles as ComponentStyles | Record<string, unknown>;
    
    if (stylesObj && typeof stylesObj === 'object') {
      // Handle ComponentStyles structure
      if ('layout' in stylesObj && typeof stylesObj.layout === 'object') {
        const layout = stylesObj.layout as Record<string, string>;
        if (layout.width) computedStyles.width = layout.width;
        if (layout.height) computedStyles.height = layout.height;
        if (layout.padding) computedStyles.padding = layout.padding;
        if (layout.paddingTop) computedStyles.paddingTop = layout.paddingTop;
        if (layout.paddingRight) computedStyles.paddingRight = layout.paddingRight;
        if (layout.paddingBottom) computedStyles.paddingBottom = layout.paddingBottom;
        if (layout.paddingLeft) computedStyles.paddingLeft = layout.paddingLeft;
        if (layout.margin) computedStyles.margin = layout.margin;
        if (layout.marginTop) computedStyles.marginTop = layout.marginTop;
        if (layout.marginRight) computedStyles.marginRight = layout.marginRight;
        if (layout.marginBottom) computedStyles.marginBottom = layout.marginBottom;
        if (layout.marginLeft) computedStyles.marginLeft = layout.marginLeft;
        if (layout.display) computedStyles.display = layout.display as React.CSSProperties['display'];
        if (layout.justifyContent) computedStyles.justifyContent = layout.justifyContent as React.CSSProperties['justifyContent'];
        if (layout.alignItems) computedStyles.alignItems = layout.alignItems as React.CSSProperties['alignItems'];
      }
      
      if ('typography' in stylesObj && typeof stylesObj.typography === 'object') {
        const typography = stylesObj.typography as Record<string, string>;
        if (typography.fontSize) computedStyles.fontSize = typography.fontSize;
        if (typography.fontWeight) computedStyles.fontWeight = typography.fontWeight;
        if (typography.color) computedStyles.color = typography.color;
        if (typography.textAlign) computedStyles.textAlign = typography.textAlign as React.CSSProperties['textAlign'];
      }
      
      if ('textColor' in stylesObj && typeof stylesObj.textColor === 'string') {
        computedStyles.color = stylesObj.textColor as string;
      }
      
      if ('appearance' in stylesObj && typeof stylesObj.appearance === 'object') {
        const appearance = stylesObj.appearance as Record<string, string>;
        if (appearance.backgroundColor) computedStyles.backgroundColor = appearance.backgroundColor;
        if (appearance.borderRadius) computedStyles.borderRadius = appearance.borderRadius;
        if (appearance.borderWidth) computedStyles.borderWidth = appearance.borderWidth;
        if (appearance.borderStyle) computedStyles.borderStyle = appearance.borderStyle as React.CSSProperties['borderStyle'];
        if (appearance.borderColor) computedStyles.borderColor = appearance.borderColor;
        if (appearance.boxShadow) computedStyles.boxShadow = appearance.boxShadow;
      }
      
      // If styles is a raw object, merge all properties directly
      if (!('layout' in stylesObj || 'typography' in stylesObj || 'appearance' in stylesObj)) {
        Object.assign(computedStyles, stylesObj);
      }
    }
  }

  return computedStyles;
}

/**
 * Split computed styles into two layers:
 * - outerStyles: sizing, margins, borders, and background
 * - contentStyles: padding, alignment, and typography
 *
 * This keeps padding inside the background instead of expanding/moving it.
 */
function splitStylesForLayers(styles: React.CSSProperties) {
  const outerStyles: React.CSSProperties = {};
  const contentStyles: React.CSSProperties = {};

  const outerKeys = new Set([
    'margin',
    'marginTop',
    'marginRight',
    'marginBottom',
    'marginLeft',
    'width',
    'height',
    'minWidth',
    'maxWidth',
    'minHeight',
    'maxHeight',
    'background',
    'backgroundColor',
    'backgroundImage',
    'backgroundSize',
    'backgroundPosition',
    'backgroundRepeat',
    'backgroundAttachment',
    'backgroundClip',
    'backgroundOrigin',
    'backgroundBlendMode',
    'borderRadius',
    'borderColor',
    'borderWidth',
    'borderStyle',
    'boxShadow',
    'overflow',
    'position',
    'top',
    'right',
    'bottom',
    'left',
  ]);

  Object.entries(styles).forEach(([key, value]) => {
    if (value === undefined) return;
    if (outerKeys.has(key)) {
      (outerStyles as Record<string, unknown>)[key] = value;
    } else {
      (contentStyles as Record<string, unknown>)[key] = value;
    }
  });

  return { outerStyles, contentStyles };
}

/**
 * Helper to convert hex color to RGB
 */
function hexToRgb(hex: string): [number, number, number] | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
      ]
    : null;
}

export default function SectionRenderer({ section, isFirstSection = false, sectionIndex = 0, mediaUrls = [], useNeuroplasticV2 = false, mediaPriority = 'NORMAL' }: SectionRendererProps) {
  // Get background image from sectionData if available
  // Hooks must be called before any early returns to maintain consistent hook order
  const backgroundImage = useMemo(() => {
    if (!section?.sectionData?.backgroundImage) {
      return undefined;
    }
    const bg = section.sectionData.backgroundImage;
    if (typeof bg === 'string' || (typeof bg === 'object' && bg !== null && ('desktop' in bg || 'mobile' in bg))) {
      return bg as ResponsiveBackgroundImage | string;
    }
    return undefined;
  }, [section?.sectionData]);

  // Use mediaPriority from PageRenderer for consistent priority handling
  const shouldPrioritize = mediaPriority === 'CRITICAL' || mediaPriority === 'HIGH';
  const isLowPriority = mediaPriority === 'LOW' || mediaPriority === 'BACKGROUND';

  // Extract fallback images from section for immediate preload
  const fallbackImageUrls = useMemo(() => {
    if (!section) return [];
    const urls: string[] = [];
    const seen = new Set<string>();
    
    // Extract for both mobile and desktop to get all possible fallback images
    const fallbackImagesMobile = extractFallbackImagesFromSection(section, true);
    const fallbackImagesDesktop = extractFallbackImagesFromSection(section, false);
    // Combine and deduplicate
    const allFallbackImages = [...fallbackImagesMobile, ...fallbackImagesDesktop];
    
    allFallbackImages.forEach(img => {
      if (img.src && !seen.has(img.src)) {
        seen.add(img.src);
        urls.push(img.src);
      }
    });
    
    // Also include backgroundImage from sectionData if it's an image (not video)
    if (backgroundImage) {
      if (typeof backgroundImage === 'string') {
        if (!seen.has(backgroundImage)) {
          seen.add(backgroundImage);
          urls.push(backgroundImage);
        }
      } else if (typeof backgroundImage === 'object' && backgroundImage !== null) {
        if (backgroundImage.mobile && !seen.has(backgroundImage.mobile)) {
          seen.add(backgroundImage.mobile);
          urls.push(backgroundImage.mobile);
        }
        if (backgroundImage.desktop && !seen.has(backgroundImage.desktop)) {
          seen.add(backgroundImage.desktop);
          urls.push(backgroundImage.desktop);
        }
      }
    }
    
    return urls;
  }, [section, backgroundImage]);

  // Preload fallback images with link tags for immediate loading
  // CRITICAL: Hero sections (CRITICAL priority) get highest priority
  useEffect(() => {
    if (typeof window === 'undefined' || fallbackImageUrls.length === 0) return;
    
    const preloadImage = (url: string) => {
      // Check if link already exists
      const existingLink = document.querySelector(`link[rel="preload"][as="image"][href="${url}"]`);
      if (existingLink) return;
      
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = url;
      // CRITICAL priority always gets high fetchpriority
      if (mediaPriority === 'CRITICAL') {
        link.setAttribute('fetchpriority', 'high');
      } else if (mediaPriority === 'HIGH') {
        link.setAttribute('fetchpriority', 'high');
      }
      document.head.appendChild(link);
    };
    
    // CRITICAL: For Hero sections, preload immediately and synchronously
    // This ensures fallback images are available before render
    if (mediaPriority === 'CRITICAL') {
      // Preload Hero fallback images immediately (synchronous)
      fallbackImageUrls.forEach(preloadImage);
      
      // Also preload via mediaCache with highest priority
      mediaCache.preloadBatch(fallbackImageUrls, 'high').catch(() => {
        // Silently handle errors
      });
    } else {
      // For other sections, preload asynchronously
      fallbackImageUrls.forEach(preloadImage);
      
      if (fallbackImageUrls.length > 0) {
        mediaCache.preloadBatch(fallbackImageUrls, mediaPriority === 'HIGH' ? 'high' : 'low').catch(() => {
          // Silently handle errors
        });
      }
    }
  }, [fallbackImageUrls, mediaPriority]);

  // Immediately preload media based on priority level
  useEffect(() => {
    if (mediaUrls.length === 0) return;
    
    if (mediaPriority === 'CRITICAL') {
      // Critical media (hero) - preload immediately with highest priority
      mediaCache.preloadBatch(mediaUrls, 'high').catch(() => {
        // Silently handle errors
      });
    } else if (mediaPriority === 'HIGH') {
      // High priority (above fold) - preload with high priority
      mediaCache.preloadBatch(mediaUrls, 'high').catch(() => {
        // Silently handle errors
      });
    } else if (mediaPriority === 'NORMAL') {
      // Normal priority - add to queue for background loading
      mediaCache.addToQueue(mediaUrls);
      mediaCache.processQueue('low').catch(() => {
        // Silently handle errors
      });
    } else {
      // Low/Background priority - lazy load only when needed
      mediaCache.addToQueue(mediaUrls);
    }
  }, [mediaPriority, mediaUrls]);

  // Optimize background loading based on priority
  const bgOptimization = useBackgroundOptimization(backgroundImage, {
    lazyLoad: isLowPriority,
    priority: shouldPrioritize,
    rootMargin: shouldPrioritize ? '0px' : '200px',
  });

  // Compute styles from section background and styles
  const { outerStyles, contentStyles } = useMemo(() => {
    if (!section) return { outerStyles: {}, contentStyles: {} };
    const computed = computeSectionStyles(section.background, section.styles, backgroundImage);
    return splitStylesForLayers(computed);
  }, [section?.background, section?.styles, backgroundImage]);

  const wrapWithContentStyles = (node: React.ReactNode) => {
    if (Object.keys(contentStyles).length === 0) return node;
    return <div style={contentStyles}>{node}</div>;
  };

  // Early return after all hooks are called
  if (!section || section.isActive !== true) {
    return null;
  }

  // SPACER sections should render directly without wrappers
  if (section.type === 'SPACER') {
    return (
      <SectionComponentRenderer section={section} isFirstSection={isFirstSection} sectionIndex={sectionIndex} />
    );
  }

  // APPLY_VIDEO_INTRO must use its dedicated React component so the
  // "Video Interview" CTA can randomize across the configured ctaLinks pool.
  // Skipping the neuroplastic template path is required because the template
  // emits a static <a href={ctaLink}>.
  if (section.type === 'APPLY_VIDEO_INTRO') {
    return (
      <SectionComponentRenderer section={section} isFirstSection={isFirstSection} sectionIndex={sectionIndex} />
    );
  }

  // NEUROPLASTIC V2 MODE: Use unified NeuroplasticSection component
  // This is the newest and most flexible rendering mode
  if (useNeuroplasticV2) {
    const neuroplasticConfig = transformToNeuroplasticConfig({
      id: section.id,
      sectionKey: section.sectionKey,
      title: section.title,
      type: section.type,
      sectionData: section.sectionData as Record<string, unknown>,
      styles: section.styles as Record<string, unknown>,
      background: section.background ? (section.background as unknown as Record<string, unknown>) : null,
    });

    return (
      <NeuroplasticSectionV2
        config={neuroplasticConfig}
        isFirstSection={isFirstSection}
        sectionIndex={sectionIndex}
        debug={false}
      />
    );
  }

  // Check if we should use neuroplastic V1 rendering
  // Neuroplastic V1 mode: when section has an explicit template OR type has a generator
  const hasTemplate = section.template && typeof section.template === 'object' && 'type' in section.template;
  // PERFORMANCE: Only check for template generator if no explicit template exists
  // Most sections have explicit templates, so we can skip the async check in most cases
  const [hasTemplateGenerator, setHasTemplateGenerator] = useState(false);
  useEffect(() => {
    if (!hasTemplate && section.type) {
      getTemplateGenerator(section.type).then((generator) => {
        setHasTemplateGenerator(!!generator);
      });
    }
  }, [hasTemplate, section.type]);
  const useNeuroplastic = hasTemplate || hasTemplateGenerator;

  // NEUROPLASTIC V1 MODE: Use JSON template rendering
  if (useNeuroplastic) {
    const neuroplasticSection: NeuroplasticSection = {
      id: section.id,
      sectionKey: section.sectionKey,
      title: section.title,
      type: section.type,
      template: section.template as NeuroplasticSection['template'],
      sectionData: section.sectionData as NeuroplasticSection['sectionData'],
      styles: section.styles as Record<string, unknown>,
      background: section.background as NeuroplasticSection['background'],
      isActive: section.isActive,
      order: section.order,
    };

    return (
      <SectionWrapper 
        style={outerStyles}
        ref={(el) => {
          if ('setElementRef' in bgOptimization && typeof bgOptimization.setElementRef === 'function') {
            bgOptimization.setElementRef(el);
          }
        }}
      >
        {wrapWithContentStyles(
          <NeuroplasticSectionRenderer
            section={neuroplasticSection}
            isFirstSection={isFirstSection}
            sectionIndex={sectionIndex}
            mediaUrls={mediaUrls}
            debug={false}
            applyBackground={false}
            containerStyle={{}}
          />
        )}
      </SectionWrapper>
    );
  }

  // LEGACY MODE: Use hardcoded section components (fallback)
  return (
    <SectionProvider sectionId={section.id}>
      <SectionWrapper 
        style={outerStyles}
        ref={(el) => {
          if ('setElementRef' in bgOptimization && typeof bgOptimization.setElementRef === 'function') {
            bgOptimization.setElementRef(el);
          }
        }}
      >
        {wrapWithContentStyles(
          <SectionContentWrapper>
            <SectionComponentRenderer section={section} isFirstSection={isFirstSection} sectionIndex={sectionIndex} />
          </SectionContentWrapper>
        )}
      </SectionWrapper>
    </SectionProvider>
  );
}

